import { NewApiBaseUrl } from '../consts/new-api-base-url'
import { getActiveConfigLocale, getConfigLocaleHeaders } from '../utils/config-localization'

/**
 * @typedef {Object} OfflineSyncApiResult
 * @property {boolean} ok
 * @property {unknown} data
 * @property {number} [httpStatus]
 * @property {string} [message]
 */

/**
 * Risposte API ADaMS (ApiCallResponse).
 * @param {unknown} payload
 * @returns {{ ok: boolean, data: unknown, message?: string }}
 */
export function parseApiCallResponse(payload) {
  if (payload == null || typeof payload !== 'object') {
    return { ok: false, data: null, message: 'invalid_json' }
  }
  const p = /** @type {Record<string, unknown>} */ (payload)
  const anyErr = p.responseAnyError === true
  const codeRaw = p.responseCode ?? p.response_code
  let code =
    typeof codeRaw === 'number' && !Number.isNaN(codeRaw) ? codeRaw : undefined
  if (code === undefined && (typeof codeRaw === 'string' || typeof codeRaw === 'bigint')) {
    const n = Number(codeRaw)
    if (!Number.isNaN(n)) code = n
  }
  const msg =
    typeof p.responseMessage === 'string'
      ? p.responseMessage
      : typeof p.response_message === 'string'
        ? p.response_message
        : undefined

  if (anyErr || code === -1) {
    return { ok: false, data: p.responseData ?? null, message: msg }
  }
  return { ok: true, data: p.responseData ?? null, message: msg }
}

/**
 * @param {string} token
 * @returns {Promise<OfflineSyncApiResult>}
 */
export async function fetchOfflineSyncUsers(token) {
  const url = new URL(`${NewApiBaseUrl}/OfflineSync/users`).toString()
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok) {
      return {
        ok: false,
        data: null,
        httpStatus: response.status,
        message: `HTTP ${response.status}`
      }
    }
    const parsed = parseApiCallResponse(payload)
    return {
      ok: parsed.ok,
      data: parsed.data,
      httpStatus: response.status,
      message: parsed.message
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    return { ok: false, data: null, message }
  }
}

/**
 * @param {string} token
 * @param {string | number | null} [organizationId]
 * @returns {Promise<OfflineSyncApiResult>}
 */
export async function fetchOfflineOrganizationConfigs(token, organizationId) {
  const u = new URL(`${NewApiBaseUrl}/OfflineSync/organization-configs`)
  u.searchParams.set('locale', getActiveConfigLocale())
  if (organizationId != null && organizationId !== '') {
    u.searchParams.set('organization_id', String(organizationId))
  }
  try {
    const response = await fetch(u.toString(), {
      headers: getConfigLocaleHeaders({ Authorization: `Bearer ${token}` }),
    })
    const payload = await response.json().catch(() => null)
    if (response.status === 403) {
      return { ok: false, data: null, httpStatus: 403, message: 'forbidden' }
    }
    if (!response.ok) {
      return {
        ok: false,
        data: null,
        httpStatus: response.status,
        message: `HTTP ${response.status}`
      }
    }
    const parsed = parseApiCallResponse(payload)
    return {
      ok: parsed.ok,
      data: parsed.data,
      httpStatus: response.status,
      message: parsed.message
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    return { ok: false, data: null, message }
  }
}
